import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import { CANDIDATE, ISSUES, ENDORSEMENTS } from "@/data/content";
import { MaskedText, Reveal, ClipReveal } from "@/components/campaign/Motion";
import { Marquee } from "@/components/campaign/Marquee";

/* ------------------------------------------------------------ */
/*  Manifesto chapters — three numbered acts, one per pillar.   */
/* ------------------------------------------------------------ */
const CHAPTERS = [
  {
    numeral: "I",
    number: "01",
    kicker: "Strong Communities",
    heading: ["Organized on doorsteps.", "Not in donor rooms."],
    body:
      "Hillsborough 40 deserves a representative who shows up before the election — and stays long after. As a community mobilizer in Manchester, I've spent years at kitchen tables, in packed town halls, and in coalitions with the people who actually live the policies debated in Concord.",
    image: "/img/chapter1-mural.jpg",
    caption: "Community organizing training — Manchester, NH",
    align: "left",
  },
  {
    numeral: "II",
    number: "02",
    kicker: "Secure Future",
    heading: ["Housing you can afford.", "Schools we can trust."],
    body:
      "Rent is climbing. Classrooms are stretched. Working families are asked to swallow every rising cost while Concord protects the ceiling. A secure future means public dollars for public schools, protections for tenants, and healthcare treated as a right — not a privilege.",
    image: "/img/chapter-banner.jpg",
    caption: "",
    align: "right",
  },
  {
    numeral: "III",
    number: "03",
    kicker: "Bold Solutions",
    heading: ["Civil rights.", "Real climate action."],
    body:
      "The moment demands representatives who lead, not survey. I've chaired Black Lives Matter New Hampshire and sat on the boards of SOS Recovery and Building Community NH. Bold means telling the truth about the crisis in front of us and legislating like our neighbors are counting on it — because they are.",
    image: "/img/chapter3-rally.jpg",
    caption: "Filing day — NH Secretary of State's office",
    align: "left",
  },
];

/* ------------------------------------------------------------ */
/*  Hero — kinetic dark editorial with parallax portrait.       */
/* ------------------------------------------------------------ */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0.35]);
  const arcRotate = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section
      ref={ref}
      className="relative isolate overflow-hidden bg-graphite text-cream select-terra grain min-h-[100svh] flex items-center"
      data-testid="hero-section"
    >
      {/* Layered background: soft radial spotlight + terracotta arc */}
      <motion.div
        style={{ y: bgY, opacity: bgOpacity }}
        className="absolute inset-0 -z-10 pointer-events-none"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 55% at 78% 40%, rgba(232,122,47,0.28) 0%, rgba(10,25,48,0) 60%), radial-gradient(50% 40% at 15% 85%, rgba(20,63,124,0.55) 0%, rgba(10,25,48,0) 60%)",
          }}
        />
      </motion.div>

      {/* Rotating orange arc — signature detail */}
      <motion.svg
        style={{ rotate: arcRotate }}
        viewBox="0 0 200 200"
        aria-hidden="true"
        className="absolute -right-24 -top-24 h-[520px] w-[520px] opacity-40 md:opacity-60 pointer-events-none"
      >
        <defs>
          <linearGradient id="arcGrad" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#F58A3F" stopOpacity="1" />
            <stop offset="100%" stopColor="#F58A3F" stopOpacity="0" />
          </linearGradient>
        </defs>
        <circle
          cx="100"
          cy="100"
          r="88"
          fill="none"
          stroke="url(#arcGrad)"
          strokeWidth="2"
          strokeDasharray="290 260"
        />
        <circle cx="100" cy="100" r="70" fill="none" stroke="#F58A3F" strokeOpacity="0.28" strokeWidth="1" />
        <circle cx="100" cy="100" r="54" fill="none" stroke="#FAF3E8" strokeOpacity="0.12" strokeWidth="1" />
      </motion.svg>

      <div className="container-x relative py-24 md:py-32 lg:py-36 grid gap-14 md:grid-cols-12 items-center">
        {/* Editorial text column */}
        <div className="md:col-span-7 lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
            className="inline-flex items-center gap-3 mb-8"
            data-testid="hero-overline"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-terracotta opacity-70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-terracotta" />
            </span>
            <span className="font-mono text-[11px] tracking-[0.32em] uppercase text-cream/85">
              2026 · {CANDIDATE.district} · Manchester, NH
            </span>
          </motion.div>

          <h1
            className="font-serif font-medium leading-[0.86] tracking-[-0.03em] text-[clamp(3rem,10.5vw,10rem)]"
            data-testid="hero-title"
          >
            <MaskedText text="Robert" as="span" className="block" delay={0.15} stagger={0.05} />
            <MaskedText
              text="Richard-Snipes."
              as="span"
              className="block italic text-terracotta"
              delay={0.55}
              stagger={0.05}
            />
          </h1>

          <div className="mt-10 max-w-xl">
            <MaskedText
              text="A community mobilizer running to bring grassroots voices into the Granite State legislature."
              className="block font-serif text-lg md:text-xl leading-[1.45] text-cream/85"
              delay={1.15}
              stagger={0.015}
              duration={0.7}
              data-testid="hero-tagline"
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.9, duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <Link
              to="/donate"
              className="group inline-flex items-center gap-3 bg-terracotta text-graphite px-7 py-4 rounded-full font-semibold uppercase tracking-[0.18em] text-[12px] hover:bg-cream transition-colors duration-300"
              data-testid="hero-donate-button"
            >
              Chip in
              <span className="grid h-7 w-7 place-items-center rounded-full bg-graphite text-terracotta transition-transform duration-500 group-hover:rotate-45">
                <ArrowUpRight size={14} />
              </span>
            </Link>
            <Link
              to="/volunteer"
              className="group inline-flex items-center gap-2 font-mono uppercase text-[12px] tracking-[0.24em] text-cream/80 hover:text-terracotta transition-colors"
              data-testid="hero-volunteer-button"
            >
              <span className="h-px w-8 bg-current transition-all duration-300 group-hover:w-12" />
              Volunteer
            </Link>
          </motion.div>

          {/* Bottom scroll cue */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.4, duration: 1 }}
            className="hidden md:flex absolute -bottom-6 left-0 items-center gap-3 font-mono text-[10px] tracking-[0.4em] uppercase text-cream/60"
          >
            <motion.span
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block h-6 w-px bg-cream/40"
            />
            Scroll · The Manifesto
          </motion.div>
        </div>

        {/* Portrait — clipped frame with parallax and slow reveal */}
        <div className="md:col-span-5 lg:col-span-5 relative z-10 flex justify-center md:justify-end">
          <motion.div
            style={{ y: portraitY }}
            className="relative w-[min(90vw,420px)] aspect-[4/5]"
            data-testid="hero-portrait-wrap"
          >
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0 0 0 0)" }}
              transition={{ duration: 1.6, ease: [0.19, 1, 0.22, 1], delay: 0.3 }}
              className="absolute inset-0 clip-editorial overflow-hidden"
            >
              <motion.img
                initial={{ scale: 1.18 }}
                animate={{ scale: 1 }}
                transition={{ duration: 2.2, ease: [0.19, 1, 0.22, 1], delay: 0.3 }}
                src={CANDIDATE.portrait}
                alt="Robert Richard-Snipes"
                className="h-full w-full object-cover object-center"
                data-testid="hero-portrait"
              />
              {/* Spotlight vignette */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(60% 45% at 40% 30%, rgba(255,255,255,0) 0%, rgba(10,25,48,0.35) 75%, rgba(10,25,48,0.75) 100%)",
                }}
              />
            </motion.div>

            {/* Editorial corner tag */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.9 }}
              className="absolute -bottom-6 -left-4 md:-left-6 bg-terracotta text-graphite px-4 py-2 font-mono text-[10px] uppercase tracking-[0.28em] rotate-[-3deg] shadow-lg"
            >
              Candidate — 2026
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.0, duration: 0.9 }}
              className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.28em] text-cream/70"
            >
              N 42°59′ / W 71°27′
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom rule */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-cream/15" />
    </section>
  );
};

/* ------------------------------------------------------------ */
/*  Slow editorial marquee                                       */
/* ------------------------------------------------------------ */
const HeroMarquee = () => (
  <div
    className="bg-parchment text-cream border-y border-white/10 py-5 font-serif italic text-2xl md:text-3xl"
    data-testid="editorial-marquee"
  >
    <Marquee
      speed={65}
      items={[
        "Hillsborough District 40",
        "Manchester, New Hampshire",
        "Ward 1",
        "Ward 3",
        "Ward 10",
        "Ward 11",
        "Ward 12",
        "A People's Campaign",
        "Strong Communities",
        "Secure Future",
        "Bold Solutions",
      ]}
    />
  </div>
);

/* ------------------------------------------------------------ */
/*  A single numbered chapter block.                            */
/* ------------------------------------------------------------ */
const Chapter = ({ chapter, index }) => {
  const alignLeft = chapter.align === "left";
  return (
    <section
      className="relative py-24 md:py-36 border-t border-ink/15"
      data-testid={`chapter-${chapter.number}`}
    >
      <div className="container-x">
        {/* Numeral header */}
        <div className="grid grid-cols-12 items-end gap-6 mb-14">
          <Reveal className="col-span-12 md:col-span-4" as="div">
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-terracotta">
              Chapter {chapter.numeral}
            </p>
            <p className="mt-3 font-serif font-medium italic text-[clamp(5rem,14vw,11rem)] leading-[0.85] tracking-tight text-ink/10">
              {chapter.number}
            </p>
          </Reveal>
          <Reveal className="col-span-12 md:col-span-8 md:pl-8" delay={0.1}>
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-ink/60 mb-4">
              {chapter.kicker}
            </p>
            <h3 className="font-serif font-medium tracking-[-0.02em] leading-[0.95] text-[clamp(2.25rem,5.4vw,4.5rem)] text-ink">
              {chapter.heading.map((line, i) => (
                <span key={i} className="block">
                  {i === 1 ? <em className="italic text-terracotta not-italic-hover">{line}</em> : line}
                </span>
              ))}
            </h3>
          </Reveal>
        </div>

        {/* Body + photo */}
        <div className={`grid grid-cols-12 gap-6 md:gap-10 items-center ${alignLeft ? "" : "md:flex-row-reverse"}`}>
          <Reveal
            className={`col-span-12 md:col-span-7 ${alignLeft ? "md:order-1" : "md:order-2"}`}
            delay={0.15}
          >
            <div
              className={`relative w-full aspect-[4/3] overflow-hidden ${
                index % 2 === 0 ? "clip-editorial" : "clip-editorial-alt"
              }`}
            >
              <ClipReveal
                src={chapter.image}
                alt={chapter.caption}
                wrapperClassName="w-full h-full"
                className="h-full w-full object-cover"
              />
              {/* Editorial photo credit */}
              <div className="absolute bottom-3 left-3 bg-graphite/85 text-cream px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em]">
                Fig. {chapter.number}
              </div>
            </div>
          </Reveal>
          <Reveal
            className={`col-span-12 md:col-span-5 ${alignLeft ? "md:order-2" : "md:order-1"}`}
            delay={0.25}
          >
            <p className="font-serif text-lg md:text-xl leading-[1.55] text-ink/85">
              {chapter.body}
            </p>
            {chapter.caption ? (
              <div className="mt-8 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.32em] text-ink/50">
                <span className="h-px w-10 bg-ink/30" />
                {chapter.caption}
              </div>
            ) : null}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------ */
/*  Endorsement reel — slow auto-rotating carousel.             */
/* ------------------------------------------------------------ */
const EndorsementReel = () => {
  const list = ENDORSEMENTS ?? [];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const DURATION = 7000; // ms per slide

  useEffect(() => {
    if (!list.length || paused) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % list.length);
    }, DURATION);
    return () => clearInterval(id);
  }, [list.length, paused, index]);

  if (!list.length) return null;
  const current = list[index];

  return (
    <section
      className="relative bg-graphite text-cream select-terra grain overflow-hidden py-28 md:py-40 border-y border-terracotta/40"
      data-testid="endorsement-reel-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Massive editorial quotation mark */}
      <div className="absolute -top-16 left-2 md:left-10 font-serif text-terracotta/15 text-[26rem] leading-none pointer-events-none select-none">
        “
      </div>

      <div className="container-x relative">
        <Reveal className="max-w-4xl mx-auto text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-terracotta flex items-center justify-center gap-3">
            <span className="h-px w-10 bg-terracotta" />
            Endorsements — the reel
            <span className="h-px w-10 bg-terracotta" />
          </p>
        </Reveal>

        {/* Rotating slide */}
        <div className="max-w-5xl mx-auto mt-14 min-h-[280px] md:min-h-[380px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
              className="text-center"
            >
              <blockquote className="font-serif italic font-medium tracking-[-0.02em] leading-[1.05] text-[clamp(1.75rem,4.5vw,3.75rem)] text-cream">
                <MaskedText
                  text={`“${current.quote}”`}
                  once={false}
                  stagger={0.025}
                  duration={0.8}
                  className="block"
                />
              </blockquote>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="mt-12 flex items-center justify-center gap-4"
              >
                <span className="h-px w-14 bg-terracotta" />
                <div className="text-center">
                  <div className="font-mono uppercase tracking-[0.28em] text-[11px] text-cream">
                    {current.name}
                  </div>
                  <div className="text-cream/60 text-sm mt-1">{current.title}</div>
                </div>
                <span className="h-px w-14 bg-terracotta" />
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress rail — one bar per endorser, click to jump */}
        <div
          className="mt-14 flex items-center justify-center gap-4"
          data-testid="endorsement-reel-controls"
        >
          {list.map((e, i) => (
            <button
              key={e.name}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Show endorsement from ${e.name}`}
              className="group relative h-[3px] w-16 md:w-24 overflow-hidden bg-cream/20 rounded-full cursor-pointer"
              data-testid={`endorsement-dot-${i}`}
            >
              <motion.span
                key={`${i}-${index === i ? "active" : "idle"}-${paused ? "p" : "r"}`}
                className="absolute inset-y-0 left-0 bg-terracotta rounded-full"
                initial={{ width: i < index ? "100%" : "0%" }}
                animate={{
                  width: i === index && !paused ? "100%" : i < index ? "100%" : "0%",
                }}
                transition={
                  i === index && !paused
                    ? { duration: DURATION / 1000, ease: "linear" }
                    : { duration: 0.4 }
                }
              />
              <span
                className={`absolute -bottom-6 left-1/2 -translate-x-1/2 font-mono text-[9px] uppercase tracking-[0.28em] transition-colors ${
                  i === index ? "text-terracotta" : "text-cream/40 group-hover:text-cream/70"
                }`}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------ */
/*  District wards — restyled with animated tiles.              */
/* ------------------------------------------------------------ */
const District = () => (
  <section className="relative bg-canvas py-24 md:py-32 border-t border-ink/15" data-testid="wards-section">
    <div className="container-x grid gap-14 md:grid-cols-12 items-center">
      <Reveal className="md:col-span-5 flex justify-center">
        <img
          src={CANDIDATE.logoWards}
          alt="ROB4NH serving Manchester Wards 1, 3, 10, 11, and 12"
          className="w-full max-w-sm"
          data-testid="wards-graphic"
        />
      </Reveal>
      <div className="md:col-span-7">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-terracotta">
            The District
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-serif font-medium tracking-[-0.02em] leading-[0.95] text-[clamp(2.25rem,5.4vw,4.5rem)] text-ink">
            Serving Manchester Wards <em className="italic text-terracotta">1, 3, 10, 11 &amp; 12</em>.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 text-lg text-ink/75 leading-relaxed max-w-2xl">
            Hillsborough District 40 covers some of Manchester&apos;s most diverse and hardworking
            neighborhoods. If you live, vote, or organize in any of these wards —{" "}
            <span className="italic font-medium text-terracotta text-base">this is your campaign</span>.
          </p>
        </Reveal>
        <div className="mt-10 grid grid-cols-5 gap-3 max-w-md">
          {["1", "3", "10", "11", "12"].map((w, i) => (
            <motion.div
              key={w}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ delay: 0.05 * i, duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
              whileHover={{ y: -4, backgroundColor: "#F58A3F", color: "#0A1930" }}
              className="border border-ink/25 bg-white/5 py-5 text-center font-serif font-medium text-3xl text-ink shadow-[4px_4px_0_0_#F58A3F] transition-colors"
            >
              {w}
            </motion.div>
          ))}
        </div>
        <Reveal delay={0.25}>
          <Link
            to="/vote"
            className="mt-10 inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.3em] text-forest hover:text-terracotta transition-colors"
          >
            Find your polling place
            <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------ */
/*  Issues preview — restyled as an editorial index.            */
/* ------------------------------------------------------------ */
const IssuesPreview = () => (
  <section className="relative bg-parchment py-24 md:py-32" data-testid="issues-preview-section">
    <div className="container-x">
      <div className="grid grid-cols-12 gap-6 items-end mb-14">
        <Reveal className="col-span-12 md:col-span-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-terracotta">
            The Platform — an Index
          </p>
          <h2 className="mt-4 font-serif font-medium tracking-[-0.02em] leading-[0.95] text-[clamp(2.25rem,5.4vw,4.5rem)] text-ink">
            Written <em className="italic text-terracotta">with</em> neighbors.<br />
            Not for them.
          </h2>
        </Reveal>
        <Reveal className="col-span-12 md:col-span-4 md:text-right" delay={0.1}>
          <Link
            to="/issues"
            className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.3em] text-ink hover:text-terracotta transition-colors"
            data-testid="home-issues-link"
          >
            Read the full platform
            <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      </div>

      <div className="border-t-2 border-ink/85">
        {ISSUES.slice(0, 6).map((issue, i) => (
          <motion.div
            key={issue.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ delay: i * 0.06, duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            className="group border-b border-white/10 grid grid-cols-12 gap-4 py-8 md:py-10 items-baseline hover:bg-white/[0.03] transition-colors"
            data-testid={`issue-card-${issue.pillar.toLowerCase()}`}
          >
            <div className="col-span-2 md:col-span-1 font-mono text-[11px] uppercase tracking-[0.3em] text-ink/50">
              {String(i + 1).padStart(2, "0")}
            </div>
            <div className="col-span-10 md:col-span-3 font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta">
              {issue.pillar}
            </div>
            <div className="col-span-12 md:col-span-6">
              <h3 className="font-serif font-medium text-2xl md:text-3xl text-ink leading-tight tracking-tight group-hover:italic transition-all">
                {issue.title}
              </h3>
              <p className="mt-2 text-ink/75 leading-relaxed max-w-xl">{issue.summary}</p>
            </div>
            <div className="hidden md:flex md:col-span-2 justify-end">
              <span className="grid h-10 w-10 place-items-center rounded-full border border-ink/30 text-ink/60 transition-all duration-300 group-hover:bg-terracotta group-hover:border-terracotta group-hover:text-graphite group-hover:rotate-45">
                <ArrowUpRight size={16} />
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------ */
/*  Closing CTA                                                  */
/* ------------------------------------------------------------ */
const ClosingCTA = () => (
  <section
    className="relative bg-graphite text-cream select-terra grain overflow-hidden py-28 md:py-40"
    data-testid="home-cta-section"
  >
    <div
      className="absolute inset-0 -z-10 opacity-70"
      style={{
        background:
          "radial-gradient(50% 40% at 20% 30%, rgba(232,122,47,0.4) 0%, rgba(10,25,48,0) 60%), radial-gradient(45% 40% at 80% 80%, rgba(20,63,124,0.5) 0%, rgba(10,25,48,0) 60%)",
      }}
    />
    <div className="container-x grid gap-12 md:grid-cols-12 items-center">
      <Reveal className="md:col-span-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-terracotta">
          Join the campaign
        </p>
        <h2 className="mt-6 font-serif font-medium tracking-[-0.03em] leading-[0.88] text-[clamp(2.75rem,8vw,7rem)]">
          <MaskedText
            text="Doors don't knock"
            once={false}
            stagger={0.05}
            className="block"
          />
          <MaskedText
            text="themselves."
            once={false}
            stagger={0.05}
            delay={0.15}
            className="block italic text-terracotta"
          />
        </h2>
        <p className="mt-8 text-lg text-cream/80 max-w-xl leading-relaxed">
          Whether it&apos;s an hour on a Saturday or a $5 contribution,{" "}
          <span className="italic text-terracotta">every bit</span> of grassroots
          support pushes us closer to a Concord that listens.
        </p>
      </Reveal>
      <Reveal className="md:col-span-4 flex flex-wrap gap-4 md:justify-end" delay={0.15}>
        <Link
          to="/volunteer"
          className="inline-flex items-center gap-2 border border-cream/40 text-cream px-7 py-4 rounded-full font-semibold uppercase tracking-[0.18em] text-[12px] hover:bg-cream hover:text-graphite transition-colors"
          data-testid="home-cta-volunteer"
        >
          Volunteer
        </Link>
        <Link
          to="/donate"
          className="group inline-flex items-center gap-3 bg-terracotta text-graphite px-7 py-4 rounded-full font-semibold uppercase tracking-[0.18em] text-[12px] hover:bg-cream transition-colors"
          data-testid="home-cta-donate"
        >
          Donate
          <span className="grid h-7 w-7 place-items-center rounded-full bg-graphite text-terracotta transition-transform duration-500 group-hover:rotate-45">
            <ArrowUpRight size={14} />
          </span>
        </Link>
      </Reveal>
    </div>
    <div className="mt-14 border-t border-cream/15 pt-6 flex flex-wrap items-center justify-between gap-4 container-x font-mono text-[10px] uppercase tracking-[0.32em] text-cream/50">
      <span className="flex items-center gap-2"><MapPin size={12} /> Manchester, NH — Ward 3</span>
      <span>Paid for by Snipes4ThePeople</span>
    </div>
  </section>
);

/* ------------------------------------------------------------ */
/*  Page                                                         */
/* ------------------------------------------------------------ */
const Home = () => {
  return (
    <main data-testid="home-page" className="bg-canvas text-ink">
      <Hero />
      <HeroMarquee />
      {CHAPTERS.map((c, i) => (
        <Chapter key={c.number} chapter={c} index={i} />
      ))}
      <District />
      <IssuesPreview />
      <ClosingCTA />
    </main>
  );
};

export default Home;
